import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import dig from 'object-dig';
import moment from 'moment';

import FaceIcon from '@material-ui/icons/Face';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import ReceiptIcon from '@material-ui/icons/Receipt';
import FiberPinIcon from '@material-ui/icons/FiberPin';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';

import Chip from '@material-ui/core/Chip';
import LinkMaterial from '@material-ui/core/Link';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';

import Paper from '~/components/Paper';
import Button from '~/components/Button';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import ModalDelete from '~/components/ModalDelete';
import InputDate from '~/components/Input/InputDate';
import GridContainer from '~/components/GridContainer';
import IconButton from '~/components/Button/IconButton';
import InputSelect from '~/components/Input/InputSelect';
import InputSearch from '~/components/Input/InputSearch';
import LinearProgress from '~/components/LinearProgress';
import tableStyles from '~/components/Table/Table/styles';
import InputControl from '~/components/Input/InputControl';
import TablePagination from '~/components/Table/TablePagination';
import InputSelectAsync from '~/components/Input/InputSelectAsync';

import { pinStatusOptions } from '~/config/constants';
import { historyPropType } from '~/config/routerPropTypes';
import { showPerson, showProduct, showPin, createPin } from '~/config/routes';

import toInt from '~/utils/Number/toInt';
import { fetchUsers } from '~/utils/fetchUsers';
import Request from '~/providers/Request';
import { AbilityContext } from '~/contexts/Can';

import PackContext from './context';

function PinList({ history }) {
  const { t } = useTranslation();
  const [modalInfo, setModalInfo] = useState({ show: false });
  const { delRecord } = useContext(PackContext);
  const ability = useContext(AbilityContext);
  const classesTable = tableStyles();

  const [loading, setLoading] = useState(false);
  const [pins, setPins] = useState([]);
  const [totalPins, setTotalPins] = useState(0);

  const [dateType, setDateType] = useState('created_at');
  const [dataInit, setDataInit] = useState(moment().startOf('month').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('month').format());
  const [user, setUser] = useState(null);
  const [product, setProduct] = useState(null);
  const [productsOptions, setProductsOptions] = useState([]);

  const [inputSearch, setInputSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const trAttr = (attr) => t(`pins.attributes.${attr}`);

  function handleDeletionModal(id) {
    setModalInfo({ show: true, id });
  }

  async function handleDelete() {
    try {
      await Request.del(`/admin/pins/${modalInfo.id}`);
      delRecord(modalInfo.id);
      getPins();
      toast.success(t('delete-toast-success'));
    } catch (e) {
      toast.error(t('delete-toast-fail'));
    } finally {
      setModalInfo({ show: false });
    }
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  async function fetchProducts() {
    Request.get('/admin/products', null, { normalizeJSON: true })
      .then((response) => {
        setProductsOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setProductsOptions([]);
      });
  }

  async function getPins() {
    setLoading(true);

    const filters = {
      dateType: dateType,
      start: dataInit,
      end: dataEnd,
      qs: inputSearch,
      page: page + 1,
      per_page: rowsPerPage,
      product_id: dig(product, 'id'),
      user_id: dig(user, 'id'),
    };

    Request.get('/admin/pins', filters, { normalizeJSON: false })
      .then((response) => {
        const { data, meta } = response;
        setLoading(false);
        setPins(data);
        setPage(toInt(meta.page) - 1);
        setTotalPins(toInt(meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    getPins();
  }, [
    inputSearch,
    page,
    rowsPerPage,
    product,
    user,
    dateType,
    dataInit,
    dataEnd,
  ]);

  return (
    <>
      <Paper Icon={FiberPinIcon} iconColor="blue" title={t('pins.defPlural')}>
        <GridContainer>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}></GridItem>
          <GridItem
            xs={12}
            sm={6}
            md={6}
            lg={6}
            xl={6}
            style={{ display: 'block', textAlign: 'right' }}
          >
            {ability.can('new', 'Pins') && (
              <Button
                color="success"
                onClick={() => {
                  history.push(createPin.reverse());
                }}
              >
                {t('action.new')}
              </Button>
            )}
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              name={'dateType'}
              value={dateType}
              label={t('pins.extras.dateType')}
              inputComponent={InputSelect}
              onChange={({ dateType }) => {
                setDateType(dateType);
              }}
              InputProps={{
                options: [
                  {
                    id: 'created_at',
                    name: 'De criação',
                  },
                  {
                    id: 'activated_at',
                    name: 'De ativação',
                  },
                  {
                    id: 'desactived_at',
                    name: 'De desativação',
                  },
                  {
                    id: 'expires_at',
                    name: 'De expiração',
                  },
                ],
                outValueType: 'string',
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataInit}
              name="date"
              label={t('pins.extras.date_init')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) => {
                setDataInit(moment(date).startOf('day').format());
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataEnd}
              name="date"
              label={t('pins.extras.date_end')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) =>
                setDataEnd(moment(date).endOf('day').format())
              }
            />
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputControl
              name={'product'}
              value={product}
              label={trAttr('product')}
              inputComponent={InputSelect}
              onChange={({ product }) => {
                setProduct(product);
              }}
              InputProps={{
                isClearable: true,
                labelKey: 'description',
                options: productsOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputControl
              name={'user'}
              value={user}
              label={trAttr('user')}
              inputComponent={InputSelectAsync}
              onChange={({ user }) => {
                setUser(user);
              }}
              InputProps={{
                isClearable: true,
                handleFetch: fetchUsers,
                createEnabled: false,
                error: false,
              }}
            />
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputSearch
              value={inputSearch}
              label={t('action.search')}
              placeholder={t('pins.extras.search')}
              onChange={setInputSearch}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <Table
          emptyData={pins.length === 0}
          loading={loading}
          columns={
            <>
              <TableCell className={classesTable.cell}>
                {t('action.actions')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('id')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('code')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('pan')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('status')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('is_active')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('user')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('product')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('activated_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('expires_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('desactived_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('product_quantity')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('quantity_used')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('quantity_pending')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('batch')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('serial')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('created_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('updated_at')}
              </TableCell>
            </>
          }
        >
          {pins.map((row) => {
            return (
              <TableRow key={row.id} hover className={classesTable.row}>
                <TableCell>
                  {ability.can('show', 'Pins') && (
                    <Link to={showPin.reverse({ id: row.id })}>
                      <IconButton
                        tooltip={t('action.show')}
                        Icon={RemoveRedEyeIcon}
                        iconColor="green"
                      />
                    </Link>
                  )}
                  {ability.can('delete', 'Pins') && (
                    <IconButton
                      tooltip={t('action.delete')}
                      onClick={() => {
                        handleDeletionModal(row.id);
                      }}
                      Icon={DeleteIcon}
                      iconColor="red"
                    />
                  )}
                </TableCell>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.pan}</TableCell>
                <TableCell>{pinStatusOptions[row.status]}</TableCell>
                <TableCell>
                  {row.is_active === true ? <CheckIcon /> : <CloseIcon />}
                </TableCell>
                <TableCell>
                  {row.user ? (
                    <Chip
                      icon={<FaceIcon />}
                      label={dig(row, 'user', 'name')}
                      component={LinkMaterial}
                      href={showPerson.reverse({
                        id: dig(row, 'user', 'id'),
                      })}
                      style={{ cursor: 'pointer' }}
                      target="_blank"
                      color="primary"
                      variant="outlined"
                      onDelete={() => {}}
                      deleteIcon={<OpenInNewIcon />}
                    />
                  ) : null}
                </TableCell>
                <TableCell>
                  {row.product ? (
                    <Chip
                      icon={<ReceiptIcon />}
                      label={dig(row, 'product', 'description')}
                      component={LinkMaterial}
                      href={showProduct.reverse({
                        id: dig(row, 'product', 'id'),
                      })}
                      style={{ cursor: 'pointer' }}
                      target="_blank"
                      color="primary"
                      variant="outlined"
                      onDelete={() => {}}
                      deleteIcon={<OpenInNewIcon />}
                    />
                  ) : null}
                </TableCell>
                <TableCell>
                  {row.activated_at
                    ? moment(row.activated_at).format('DD/MM/YYYY HH:mm')
                    : null}
                </TableCell>
                <TableCell>
                  {row.expires_at
                    ? moment(row.expires_at).format('DD/MM/YYYY HH:mm')
                    : null}
                </TableCell>
                <TableCell>
                  {row.desactived_at
                    ? moment(row.updatedesactived_atd_at).format(
                        'DD/MM/YYYY HH:mm'
                      )
                    : null}
                </TableCell>
                <TableCell>{dig(row, 'product', 'quantity')}</TableCell>
                <TableCell>{row.quantity_used}</TableCell>
                <TableCell>
                  {dig(row, 'product', 'quantity') - dig(row, 'quantity_used')}
                </TableCell>
                <TableCell>
                  {dig(row, 'batch')}
                </TableCell>
                <TableCell>
                  {dig(row, 'serial')}
                </TableCell>
                <TableCell>
                  {row.created_at
                    ? moment(row.created_at).format('DD/MM/YYYY HH:mm')
                    : null}
                </TableCell>
                <TableCell>
                  {row.updated_at
                    ? moment(row.updated_at).format('DD/MM/YYYY HH:mm')
                    : null}
                </TableCell>
              </TableRow>
            );
          })}
        </Table>

        <LinearProgress loading={loading} />

        <Typography
          variant="subtitle2"
          color="primary"
          align="right"
          style={{ paddingRight: 16, marginTop: 10 }}
        >
          <strong>Total: </strong>
          {totalPins}
        </Typography>

        <TablePagination
          count={totalPins}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />

        <ModalDelete
          id={modalInfo.id}
          modal={modalInfo.show}
          deleteAction={handleDelete}
          onClose={() => setModalInfo({ show: false })}
        />
      </Paper>
    </>
  );
}

PinList.propTypes = {
  history: historyPropType,
};

export default React.memo(PinList);
